import { useState, useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { Item } from './item';

interface SelectProps<T> {
  className?: string;
  values?: T[];
  value?: any;
  placeHolder?: string;
  view?: React.ComponentType<ItemProps<T>>;
  dropdownItem?: any;
  autoSelectFirst?: boolean;
  keyField?: keyof T;
  mapValue?: (value: T) => any;
  onChange?: (value: any) => void;
  onStateChange?: (state: { open: boolean }) => void;
}

interface ItemProps<T> {
  value: T;
  onClick?: (e: React.MouseEvent, value: T) => void;
  mode?: string;
  index?: number;
  active?: boolean;
  placeHolder?: string;
}

export function Select<T>({
  className,
  values = [],
  value: propValue,
  placeHolder,
  view: View = Item as React.ComponentType<ItemProps<T>>,
  autoSelectFirst = false,
  keyField,
  mapValue,
  onChange,
  onStateChange,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<any>();
  const [itemIndex, setItemIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const map = useCallback(
    (val: T): any => {
      if (keyField) return (val as any)[keyField];
      if (mapValue) return mapValue(val);
      return val;
    },
    [keyField, mapValue],
  );

  const getValueIndex = useCallback(
    (val: any): number => {
      return values.findIndex((v) => map(v) === val);
    },
    [values, map],
  );

  const refreshState = useCallback(
    (incomingValue?: any): any => {
      let newValue = incomingValue;
      if (!newValue && value) {
        const index = getValueIndex(value);
        if (index >= 0) newValue = value;
      }
      if (!newValue && autoSelectFirst && values.length > 0) {
        newValue = map(values[0]);
      }
      if (newValue) {
        setValue(map(newValue));
      }
    },
    [autoSelectFirst, getValueIndex, map, value, values],
  );

  useEffect(() => {
    refreshState(propValue);
  }, [propValue]);

  useEffect(() => {
    if (value !== propValue) onChange?.(value);
  }, [value]);

  useEffect(() => {
    onStateChange?.({ open });
  }, [open]);

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) doCloseUp();
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doDropDown = () => {
    setItemIndex(getValueIndex(value));
    setOpen(true);
  };
  const doCloseUp = (selectedValue?: T) => {
    setOpen(false);
    if (selectedValue) setValue(map(selectedValue));
  };
  const handleSelect = (selected: T) => doCloseUp(selected);
  const toggleDropDown = () => (open ? doCloseUp() : doDropDown());
  const selectItemByIndex = (index: number) => {
    setItemIndex(index);
    if (!dropdownRef.current) return;
    const item = dropdownRef.current.querySelector(`div:nth-child(${index + 1})`) as HTMLElement;
    if (!item) return;

    const scrollTop = dropdownRef.current.scrollTop;
    const offsetHeight = dropdownRef.current.offsetHeight;
    const itemTop = item.offsetTop;
    const itemHeight = item.offsetHeight;

    if (itemTop - scrollTop < 0) {
      dropdownRef.current.scrollTop = itemTop;
    } else if (itemTop + itemHeight - scrollTop > offsetHeight) {
      dropdownRef.current.scrollTop = itemTop + itemHeight - offsetHeight;
    }
  };
  const findWithStart = (text: string) => {
    const matchIndex = values.findIndex((item) => typeof item === 'string' && item.toUpperCase().startsWith(text.toUpperCase()));
    if (matchIndex >= 0) selectItemByIndex(matchIndex);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (!values.length) return;

    if (e.key === 'ArrowDown') {
      selectItemByIndex(itemIndex === -1 || itemIndex == null ? 0 : Math.min(itemIndex + 1, values.length - 1));
    } else if (e.key === 'ArrowUp') {
      selectItemByIndex(itemIndex === -1 || itemIndex == null ? 0 : Math.max(itemIndex - 1, 0));
    } else if (e.key === 'Escape') {
      doCloseUp();
    } else if (e.key === 'Enter' && itemIndex >= 0) {
      handleSelect(values[itemIndex]);
    } else if (e.key.length === 1) {
      findWithStart(e.key);
    }
  };

  return (
    <div className={clsx('border border-[#aaaab2] relative w-[250px]', className)} onKeyDown={handleKeyDown} tabIndex={-1} ref={dropdownRef}>
      <View value={value} placeHolder={placeHolder} onClick={toggleDropDown} />
      {open && (
        <div className='insignia-cards-ui-select__drop-down'>
          {values.map((item, i) => (
            <View value={item} onClick={(_, v) => handleSelect(v)} mode='item' key={i} index={i} active={i === itemIndex} />
          ))}
        </div>
      )}
    </div>
  );
}
